import React, { type Dispatch, type SetStateAction } from "react";

import { useSelector, useDispatch } from "react-redux";
import Tooltip from "@material-ui/core/Tooltip";

import type { VintedIntegration } from "src/interfaces/plaidIntegration.interface";
import { type DataType } from "src/pages/GetStarted/ImportDialog";
import type MonthYear from "src/interfaces/monthYear.interface";
import { isVintedAllowedToDirectImportSelector } from "src/store/uploads/selector";
import {
  vintedIntegrationDelete,
  vintedIntegrationSyncChange,
} from "src/apiService/modules/vinted";
import {
  vintedSyncWithCheck,
  vintedSyncInventory,
  vintedSyncSales,
} from "src/store/integrationSync/actions/vinted";
import { getVintedAbandonedResumeStatus } from "src/store/integrationSync/actions/vinted/vintedResumeOffer";
import { VintedSyncLockTtlMs } from "src/store/integrationSync/actions/syncLock";
import { bankAccountByIntegrationIdSelector } from "src/store/bankAccount/selector";

import PlatformIntegrationsTableRow from "../PlatformIntegrationsTableRow";
import useIsRunning from "../useIsRunning";
import type { DialogState } from "../types";

export function usePreventImportVinted(dataType: DataType) {
  return !useSelector((state: any) =>
    isVintedAllowedToDirectImportSelector(state, dataType)
  );
}

export const VintedNotice =
  "The Vinted integration is still in beta testing. If you don’t wish to use this feature, you can delete the integration by clicking the red trash can icon on the ‘Vinted’ line.";
export const VintedNoticeNode = (
  <Tooltip title={VintedNotice}>
    <span>Vinted*</span>
  </Tooltip>
);

function VintedIntegrationTableRow({
  integration,
  setDialog,
  hideInventory,
}: {
  integration: VintedIntegration;
  setDialog: Dispatch<SetStateAction<DialogState>>;
  hideInventory: boolean;
}) {
  const dispatch = useDispatch();
  const isRunning = useIsRunning(
    integration.salesIsRunning,
    integration.inventoryIsRunning,
    VintedSyncLockTtlMs
  );
  const bankAccount = useSelector((state) =>
    integration?.id
      ? bankAccountByIntegrationIdSelector(state, integration.id)
      : undefined
  );

  const importProps = {
    integrationId: integration.id,
    fetchListings: (integrationId: string) =>
      dispatch(vintedSyncInventory(integrationId)),
    fetchTransactions: async (
      integrationId: string,
      start: MonthYear,
      end: MonthYear
    ) => dispatch(vintedSyncSales(integrationId, { start, end })),
    providerName: "Vinted",
    usePreventImport: usePreventImportVinted,
  };

  return (
    <PlatformIntegrationsTableRow
      key={integration.id}
      name={bankAccount?.name || "Vinted"}
      platform={VintedNoticeNode}
      isRunning={isRunning}
      sync={integration.sync}
      inventory={integration.sync && integration.inventory !== false}
      onPullDataNow={async () => {
        await dispatch(vintedSyncWithCheck(integration.id, true));
      }}
      onImport={() => {
        const offer = getVintedAbandonedResumeStatus(integration);
        if (offer) {
          setDialog({
            type: "resume-or-import",
            props: {
              offer,
              platform: "vinted",
              importProps,
            },
          });
          return;
        }
        setDialog({
          type: "import",
          props: importProps,
        });
      }}
      onSyncChanged={async (sync, inventory) => {
        await vintedIntegrationSyncChange(integration.id, sync, inventory);
        if (sync) await dispatch(vintedSyncWithCheck(integration.id, true));
      }}
      hideInventory={hideInventory}
      onDelete={() =>
        setDialog({
          type: "delete",
          props: {
            integration,
            deleteIntegration: vintedIntegrationDelete,
          },
        })
      }
    />
  );
}

export default VintedIntegrationTableRow;
